import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import SnackbarUtils from '../../utils/SnackbarUtils';
import { getSuppliers } from '../../services/SupplierService';
import PageHeader from '../../components/PageHeader/PageHeader';
import { getIcon } from '../../utils/IconHandler';
import {
  EXPENSE_CATEGORIES,
  EXPENSE_FREQUENCIES,
  addExpensePayment,
  changeExpenseAmount,
  createExpense,
  getExpenseMetadata,
  getExpenseHistory,
  getExpenseCommissionSummary,
  listExpenseOccurrences,
  listExpenses,
  updateExpense
} from '../../services/ExpenseService';

const today = dayjs().format('YYYY-MM-DD');
const currentMonthStart = dayjs().startOf('month').format('YYYY-MM-DD');
const currentMonthEnd = dayjs().endOf('month').format('YYYY-MM-DD');

const emptyForm = {
  rut: '',
  providerId: '',
  name: '',
  category: 'ALQUILER',
  notes: '',
  expenseScope: 'LOCATION',
  locationId: '',
  expenseType: 'RECURRING',
  recurrenceFrequency: 'MONTHLY',
  amount: '',
  currency: 'UYU',
  dueDate: today,
  dueDay: String(dayjs(today).date()),
  endDate: '',
  reminderDaysBeforeDue: '2',
  allocations: {}
};

const statusColor = {
  PENDING: 'warning',
  PARTIAL: 'info',
  PAID: 'success',
  CANCELLED: 'default',
  OVERDUE: 'error'
};

const statusLabels = {
  PENDING: 'Pendiente',
  PARTIAL: 'Parcial',
  PAID: 'Pagado',
  CANCELLED: 'Cancelado',
  OVERDUE: 'Vencido'
};

const categoryLabels = {
  SUELDOS: 'Sueldos',
  ALQUILER: 'Alquiler',
  SERVICIOS: 'Servicios',
  IMPUESTOS: 'Impuestos',
  MARKETING: 'Marketing',
  MANTENIMIENTO: 'Mantenimiento',
  LOGISTICA: 'Logistica',
  OTROS: 'Otros'
};

const frequencyLabels = {
  WEEKLY: 'Semanal',
  MONTHLY: 'Mensual',
  BIMONTHLY: 'Bimestral',
  QUARTERLY: 'Trimestral',
  YEARLY: 'Anual'
};

const expenseTypeLabels = {
  ONE_TIME: 'Puntual',
  RECURRING: 'Recurrente'
};

const getEnumLabel = (labels, value) => labels[value] || value || '-';

const dateFromMonthDay = (day) => {
  const parsedDay = Number(day);
  const base = dayjs(today);
  const safeDay = Math.min(Math.max(Number.isFinite(parsedDay) ? parsedDay : base.date(), 1), base.daysInMonth());
  return base.date(safeDay).format('YYYY-MM-DD');
};

export default function ExpensesPage() {
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [ruts, setRuts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [occurrences, setOccurrences] = useState([]);
  const [commissionSummary, setCommissionSummary] = useState({ totalAmount: 0, byPaymentMethod: [] });
  const [commissionExpanded, setCommissionExpanded] = useState(false);
  const [filters, setFilters] = useState({ from: currentMonthStart, to: currentMonthEnd, rut: '', category: '', status: '' });
  const [formOpen, setFormOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', paymentDate: today, notes: '' });
  const [historyTarget, setHistoryTarget] = useState(null);
  const [history, setHistory] = useState([]);
  const canViewSuppliers = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('permissions') || '[]').map(Number).includes(700);
    } catch (_) {
      return false;
    }
  }, []);

  const loadReferenceData = useCallback(async () => {
    const metadata = await getExpenseMetadata();
    const suppliersData = canViewSuppliers ? await getSuppliers(1, 500, '') : [];
    setRuts(Array.isArray(metadata?.ruts) ? metadata.ruts : []);
    setProviders(suppliersData?.providers || suppliersData || []);
  }, [canViewSuppliers]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        from: filters.from || undefined,
        to: filters.to || undefined,
        rut: isSuperAdmin ? (filters.rut || undefined) : undefined,
        category: filters.category || undefined,
        status: filters.status || undefined
      };
      const [expensesData, occurrencesData, commissionsData] = await Promise.all([
        listExpenses({ active: true }),
        listExpenseOccurrences(params),
        getExpenseCommissionSummary({ from: filters.from || undefined, to: filters.to || undefined })
      ]);
      setExpenses(Array.isArray(expensesData) ? expensesData : []);
      setOccurrences(Array.isArray(occurrencesData) ? occurrencesData : []);
      setCommissionSummary(commissionsData || { totalAmount: 0, byPaymentMethod: [] });
    } catch (error) {
      SnackbarUtils.error(error?.response?.data?.message || 'No se pudieron cargar los gastos');
    } finally {
      setLoading(false);
    }
  }, [filters, isSuperAdmin]);

  useEffect(() => {
    loadReferenceData().catch(() => SnackbarUtils.error('No se pudieron cargar datos base'));
  }, [loadReferenceData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectedRutLocations = useMemo(() => {
    if (!form.rut) return [];
    return ruts.find((item) => item.rut === form.rut)?.locations || [];
  }, [form.rut, ruts]);

  const allocationTotal = useMemo(() => (
    Object.values(form.allocations).reduce((sum, value) => sum + (Number(value) || 0), 0)
  ), [form.allocations]);
  const allocationTotalError = form.expenseScope === 'GENERAL' && Math.abs(allocationTotal - 100) > 0.01;
  const paidByExpenseId = useMemo(() => occurrences.reduce((totals, occurrence) => {
    totals[occurrence.expenseId] = (totals[occurrence.expenseId] || 0) + Number(occurrence.paidAmount || 0);
    return totals;
  }, {}), [occurrences]);
  const expensesWithPeriodPaid = useMemo(() => expenses.map((expense) => ({
    ...expense,
    periodPaidAmount: paidByExpenseId[expense.id] || 0
  })), [expenses, paidByExpenseId]);

  const resetForm = () => {
    const firstRut = ruts[0]?.rut || '';
    const firstRutLocations = ruts[0]?.locations || [];
    setForm({
      ...emptyForm,
      rut: firstRut,
      locationId: firstRutLocations[0]?.id || '',
      allocations: {}
    });
  };

  const openCreate = () => {
    setEditingExpense(null);
    resetForm();
    setFormOpen(true);
  };

  const openEdit = (expense) => {
    const allocations = {};
    (expense.allocations || []).forEach((allocation) => {
      allocations[allocation.locationId] = allocation.percentage;
    });
    const isSingleLocation = (expense.allocations || []).length === 1 && Number(expense.allocations[0]?.percentage) === 100;
    setEditingExpense(expense);
    setForm({
      ...emptyForm,
      rut: expense.rut || '',
      providerId: expense.providerId || '',
      name: expense.name || '',
      category: expense.category || 'ALQUILER',
      notes: expense.notes || '',
      expenseScope: isSingleLocation ? 'LOCATION' : 'GENERAL',
      locationId: isSingleLocation ? (expense.allocations[0]?.locationId || '') : '',
      expenseType: expense.expenseType || 'RECURRING',
      recurrenceFrequency: expense.recurrenceFrequency || 'MONTHLY',
      amount: expense.currentAmount?.amount ?? '',
      currency: expense.currentAmount?.currency || 'UYU',
      dueDate: expense.startDate || today,
      dueDay: String(expense.dueDay || dayjs(expense.startDate || today).date()),
      endDate: expense.endDate || '',
      reminderDaysBeforeDue: String(expense.reminderDaysBeforeDue?.[0] ?? 2),
      allocations
    });
    setFormOpen(true);
  };

  const submitExpense = async () => {
    const inferredRut = isSuperAdmin ? form.rut : (ruts[0]?.rut || form.rut);
    let allocations = [];
    if (form.expenseScope === 'LOCATION') {
      if (!form.locationId) {
        SnackbarUtils.warning('Seleccioná una sucursal');
        return;
      }
      allocations = [{ locationId: Number(form.locationId), percentage: 100 }];
    } else {
      allocations = Object.entries(form.allocations)
        .map(([locationId, percentage]) => ({ locationId: Number(locationId), percentage: Number(percentage) }))
        .filter((item) => item.locationId && item.percentage > 0);
      if (Math.abs(allocationTotal - 100) > 0.01) {
        SnackbarUtils.warning('La ponderación debe sumar 100%');
        return;
      }
    }

    const reminderDays = Number(form.reminderDaysBeforeDue);
    const expensePayload = { ...form };
    delete expensePayload.expenseScope;
    delete expensePayload.locationId;
    delete expensePayload.allocations;
    delete expensePayload.dueDay;
    try {
      const payload = {
        ...expensePayload,
        rut: inferredRut,
        providerId: Number(form.providerId),
        recurrenceFrequency: form.expenseType === 'RECURRING' ? (form.recurrenceFrequency || 'MONTHLY') : null,
        dueDate: form.expenseType === 'RECURRING' ? dateFromMonthDay(form.dueDay) : form.dueDate,
        expectedPaymentDate: null,
        endDate: form.endDate || null,
        notes: form.notes || null,
        reminderDaysBeforeDue: Number.isFinite(reminderDays) && reminderDays >= 0 ? [reminderDays] : [],
        allocations
      };

      if (editingExpense) {
        await updateExpense(editingExpense.id, payload);
        const nextAmount = Number(form.amount);
        const currentAmount = Number(editingExpense.currentAmount?.amount);
        const nextCurrency = form.currency || 'UYU';
        const currentCurrency = editingExpense.currentAmount?.currency || 'UYU';
        if (Number.isFinite(nextAmount) && (nextAmount !== currentAmount || nextCurrency !== currentCurrency)) {
          await changeExpenseAmount(editingExpense.id, {
            amount: nextAmount,
            currency: nextCurrency,
            effectiveFrom: today,
            notes: form.notes || null
          });
        }
        SnackbarUtils.success('Gasto actualizado');
      } else {
        await createExpense({
          ...payload,
          amount: Number(form.amount)
        });
        SnackbarUtils.success('Gasto creado');
      }
      setFormOpen(false);
      setEditingExpense(null);
      loadData();
    } catch (error) {
      SnackbarUtils.error(error?.response?.data?.message || 'No se pudo guardar el gasto');
    }
  };

  const submitPayment = async () => {
    try {
      await addExpensePayment(paymentTarget.id, {
        amount: Number(paymentForm.amount),
        paymentDate: paymentForm.paymentDate,
        notes: paymentForm.notes || null
      });
      SnackbarUtils.success('Pago registrado');
      setPaymentTarget(null);
      loadData();
    } catch (error) {
      SnackbarUtils.error(error?.response?.data?.message || 'No se pudo registrar el pago');
    }
  };

  const openHistory = async (expense) => {
    setHistoryTarget(expense);
    setHistory([]);
    try {
      const data = await getExpenseHistory(expense.id);
      setHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      SnackbarUtils.error('No se pudo cargar el historial');
    }
  };

  const occurrenceColumns = [
    { field: 'dueDate', headerName: 'Vencimiento', width: 120 },
    {
      field: 'expenseName',
      headerName: 'Gasto',
      flex: 1,
      minWidth: 180,
      renderCell: ({ row, value }) => {
        if (row.rowType === 'commission-summary') {
          return <Button size="small" onClick={() => setCommissionExpanded((expanded) => !expanded)} startIcon={getIcon(commissionExpanded ? 'chevronDown' : 'chevronForward')} sx={{ fontWeight: 700, textTransform: 'none' }}>Comisiones</Button>;
        }
        if (row.rowType === 'commission-detail') return <Box sx={{ pl: 4, color: 'text.secondary' }}>{value}</Box>;
        return value;
      }
    },
    { field: 'providerName', headerName: 'Proveedor', flex: 1, minWidth: 160 },
    {
      field: 'amount',
      headerName: 'Importe',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => Number(value || 0).toFixed(2)
    },
    {
      field: 'paidAmount',
      headerName: 'Pagado',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => value == null ? '-' : Number(value).toFixed(2)
    },
    {
      field: 'computedStatus',
      headerName: 'Estado',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row, value }) => {
        if (row.rowType === 'commission-summary' || row.rowType === 'commission-detail') return 'Comisiones';
        return <Chip size="small" label={getEnumLabel(statusLabels, value)} color={statusColor[value] || 'default'} />;
      }
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 180,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => {
        if (row.rowType === 'commission-summary') return null;
        if (row.rowType === 'commission-detail') return null;
        return (
        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ width: '100%', height: '100%' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={getIcon('money')}
            disabled={['PAID', 'CANCELLED'].includes(row.computedStatus)}
            onClick={() => {
              setPaymentTarget(row);
              setPaymentForm({ amount: row.pendingAmount || row.amount, paymentDate: today, notes: '' });
            }}
          >
            Pagar
          </Button>
        </Stack>
        );
      }
    }
  ];

  const expenseColumns = [
    {
      field: 'name',
      headerName: 'Gasto',
      flex: 1,
      minWidth: 180,
      renderCell: ({ row, value }) => {
        if (row.rowType === 'commission-summary') return <Button size="small" onClick={() => setCommissionExpanded((expanded) => !expanded)} startIcon={getIcon(commissionExpanded ? 'chevronDown' : 'chevronForward')} sx={{ fontWeight: 700, textTransform: 'none' }}>Comisiones</Button>;
        if (row.rowType === 'commission-detail') return <Box sx={{ pl: 4, color: 'text.secondary' }}>{value}</Box>;
        return value;
      }
    },
    { field: 'providerName', headerName: 'Proveedor', flex: 1, minWidth: 160 },
    {
      field: 'category',
      headerName: 'Categoría',
      width: 130,
      valueFormatter: (value) => getEnumLabel(categoryLabels, value),
      renderCell: ({ row, value }) => {
        if (row.rowType === 'commission-summary' || row.rowType === 'commission-detail') return 'Comisiones';
        return getEnumLabel(categoryLabels, value);
      }
    },
    {
      field: 'expenseType',
      headerName: 'Tipo',
      width: 120,
      valueFormatter: (value) => getEnumLabel(expenseTypeLabels, value)
    },
    {
      field: 'recurrenceFrequency',
      headerName: 'Frecuencia',
      width: 130,
      valueFormatter: (value) => getEnumLabel(frequencyLabels, value)
    },
    {
      field: 'periodPaidAmount',
      headerName: 'Abonado',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => value == null ? '-' : Number(value).toFixed(2)
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 150,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }) => {
        if (row.rowType === 'commission-summary') return null;
        if (row.rowType === 'commission-detail') return null;
        return (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ width: '100%', height: '100%' }}>
          <Tooltip title="Editar gasto">
            <IconButton sx={{ width: 40, height: 40 }} size="small" onClick={() => openEdit(row)}>
              {getIcon('settings')}
            </IconButton>
          </Tooltip>
          <Tooltip title="Historial">
            <IconButton sx={{ width: 40, height: 40 }} size="small" onClick={() => openHistory(row)}>
              {getIcon('analytics')}
            </IconButton>
          </Tooltip>
        </Stack>
        );
      }
    }
  ];

  const commissionRows = useMemo(() => {
    const summaryRow = {
      id: 'automatic-commissions',
      rowType: 'commission-summary',
      name: 'Comisiones',
      providerName: 'Ventas',
      category: 'COMISIONES',
      expenseType: '',
      recurrenceFrequency: 'Período seleccionado',
      periodPaidAmount: Number(commissionSummary.totalAmount || 0)
    };
    if (!commissionExpanded) return [summaryRow];
    return [
      summaryRow,
      ...(commissionSummary.byPaymentMethod || []).map((item, index) => ({
        id: `automatic-commission-${index}-${item.name}`,
        rowType: 'commission-detail',
        name: item.name || 'Medio de pago',
        providerName: 'Ventas',
        category: 'COMISIONES',
        expenseType: '',
        recurrenceFrequency: '',
        periodPaidAmount: Number(item.commissionAmount || 0)
      }))
    ];
  }, [commissionExpanded, commissionSummary]);

  const paidExpenseRows = expensesWithPeriodPaid.filter((expense) => expense.periodPaidAmount > 0);
  const tableRows = tab === 0 ? occurrences : [...paidExpenseRows, ...(Number(commissionSummary.totalAmount || 0) > 0 ? commissionRows : [])];
  const tableColumns = tab === 0 ? occurrenceColumns : expenseColumns;
  const shouldShowFooter = tableRows.length > 25;
  const tableHeight = tableRows.length === 0
    ? 180
    : Math.min(580, 58 + (tableRows.length * 52) + (shouldShowFooter ? 52 : 0));

  return (
    <div>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="expenses tabs">
          <Tab label="Pagos pendientes" />
          <Tab label="Pagos realizados" />
        </Tabs>
      </Box>

      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <PageHeader
            title={tab === 0 ? 'Pagos pendientes' : 'Pagos realizados'}
            subtitle={tab === 0
              ? 'Consultá vencimientos pendientes y registrá los pagos de cada gasto.'
              : 'Revisá los gastos fijos abonados y las comisiones del período seleccionado.'}
          />
        </Box>

        <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2.4}>
                <TextField fullWidth size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }} value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={2.4}>
                <TextField fullWidth size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
              </Grid>
              {isSuperAdmin && (
                <Grid item xs={12} md={2.4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>RUT</InputLabel>
                    <Select label="RUT" value={filters.rut} onChange={(e) => setFilters((f) => ({ ...f, rut: e.target.value }))}>
                      <MenuItem value="">Todos</MenuItem>
                      {ruts.map((rut) => <MenuItem key={rut.rut} value={rut.rut}>{rut.rut}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} md={isSuperAdmin ? 2.4 : 3.6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Categoría</InputLabel>
                  <Select label="Categoría" value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}>
                    <MenuItem value="">Todas</MenuItem>
                    {EXPENSE_CATEGORIES.map((category) => (
                      <MenuItem key={category} value={category}>{getEnumLabel(categoryLabels, category)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={isSuperAdmin ? 2.4 : 3.6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado</InputLabel>
                  <Select label="Estado" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
                    <MenuItem value="">Todos</MenuItem>
                    {['PENDING', 'PARTIAL', 'PAID', 'CANCELLED', 'OVERDUE'].map((status) => (
                      <MenuItem key={status} value={status}>{getEnumLabel(statusLabels, status)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
        </Paper>

        <Paper>
          <Box sx={{ width: '100%', height: tableHeight }}>
            <DataGrid
              loading={loading}
              rows={tableRows}
              columns={tableColumns}
              disableRowSelectionOnClick
              hideFooter={!shouldShowFooter}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              sx={{
                '--DataGrid-overlayHeight': '120px'
              }}
            />
          </Box>
        </Paper>
      </Box>

      <Fab variant="extended" color="primary" onClick={openCreate} sx={{ position: 'fixed', right: 28, bottom: 28, zIndex: (theme) => theme.zIndex.speedDial }}>
        {getIcon('add')}
        <Box component="span" sx={{ ml: 1 }}>Agregar gasto</Box>
      </Fab>

      <Dialog open={formOpen} onClose={() => {
        setFormOpen(false);
        setEditingExpense(null);
      }} maxWidth="md" fullWidth>
        <DialogTitle>{editingExpense ? 'Editar gasto' : 'Nuevo gasto'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Divider textAlign="left">Datos del gasto</Divider>
            </Grid>
            {isSuperAdmin && (
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>RUT</InputLabel>
                  <Select
                    label="RUT"
                    value={form.rut}
                    onChange={(e) => {
                      const rutLocations = ruts.find((item) => item.rut === e.target.value)?.locations || [];
                      setForm((f) => ({
                        ...f,
                        rut: e.target.value,
                        locationId: rutLocations[0]?.id || '',
                        allocations: {}
                      }));
                    }}
                  >
                    {ruts.map((rut) => <MenuItem key={rut.rut} value={rut.rut}>{rut.rut}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={isSuperAdmin ? 4 : 6}>
              <FormControl fullWidth size="small">
                <InputLabel>Proveedor</InputLabel>
                <Select label="Proveedor" value={form.providerId} onChange={(e) => setForm((f) => ({ ...f, providerId: e.target.value }))}>
                  {providers.map((provider) => <MenuItem key={provider.id} value={provider.id}>{provider.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={isSuperAdmin ? 4 : 6}>
              <FormControl fullWidth size="small">
                <InputLabel>Categoría</InputLabel>
                <Select label="Categoría" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                  {EXPENSE_CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>{getEnumLabel(categoryLabels, category)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField fullWidth size="small" label="Nombre" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" type="number" label={editingExpense ? 'Importe actual' : 'Importe inicial'} value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <Divider textAlign="left">Vencimiento</Divider>
            </Grid>
            <Grid item xs={12} md={form.expenseType === 'RECURRING' ? 3 : 4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  label="Tipo"
                  value={form.expenseType}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    expenseType: e.target.value,
                    recurrenceFrequency: e.target.value === 'RECURRING' ? (f.recurrenceFrequency || 'MONTHLY') : ''
                  }))}
                >
                  <MenuItem value="ONE_TIME">Puntual</MenuItem>
                  <MenuItem value="RECURRING">Recurrente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {form.expenseType === 'RECURRING' && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Frecuencia</InputLabel>
                  <Select label="Frecuencia" value={form.recurrenceFrequency || 'MONTHLY'} onChange={(e) => setForm((f) => ({ ...f, recurrenceFrequency: e.target.value }))}>
                    {EXPENSE_FREQUENCIES.map((frequency) => (
                      <MenuItem key={frequency} value={frequency}>{getEnumLabel(frequencyLabels, frequency)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={form.expenseType === 'RECURRING' ? 3 : 4}>
              {form.expenseType === 'RECURRING' ? (
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 1, max: 31 }}
                  label="Día de vencimiento"
                  value={form.dueDay}
                  onChange={(e) => setForm((f) => ({ ...f, dueDay: e.target.value }))}
                />
              ) : (
                <TextField fullWidth size="small" type="date" label="Vencimiento" InputLabelProps={{ shrink: true }} value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} />
              )}
            </Grid>
            <Grid item xs={12} md={form.expenseType === 'RECURRING' ? 3 : 4}>
              <TextField fullWidth size="small" type="number" inputProps={{ min: 0 }} label="Recordatorios (días)" value={form.reminderDaysBeforeDue} onChange={(e) => setForm((f) => ({ ...f, reminderDaysBeforeDue: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <Divider textAlign="left">Alcance</Divider>
            </Grid>
            <Grid item xs={12} md={form.expenseScope === 'LOCATION' ? 4 : 12}>
              <FormControl fullWidth size="small">
                <InputLabel>Alcance</InputLabel>
                <Select
                  label="Alcance"
                  value={form.expenseScope}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    expenseScope: e.target.value,
                    locationId: e.target.value === 'LOCATION' ? (selectedRutLocations[0]?.id || '') : '',
                    allocations: {}
                  }))}
                >
                  <MenuItem value="LOCATION">Por sucursal</MenuItem>
                  <MenuItem value="GENERAL">General</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {form.expenseScope === 'LOCATION' && (
              <Grid item xs={12} md={8}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sucursal</InputLabel>
                  <Select label="Sucursal" value={form.locationId} onChange={(e) => setForm((f) => ({ ...f, locationId: e.target.value }))}>
                    {selectedRutLocations.map((location) => (
                      <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {form.expenseScope === 'GENERAL' && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color={allocationTotalError ? 'error' : 'text.primary'} sx={{ mb: 1 }}>
                  Ponderación por sucursal: {allocationTotal.toFixed(2)}%
                  {allocationTotalError ? ' - la suma debe ser 100%' : ''}
                </Typography>
                <Grid container spacing={1}>
                  {selectedRutLocations.map((location) => (
                    <Grid item xs={12} md={4} key={location.id}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        inputProps={{ min: 0, max: 100 }}
                        error={allocationTotalError}
                        label={location.name}
                        value={form.allocations[location.id] || ''}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          allocations: { ...f.allocations, [location.id]: e.target.value }
                        }))}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <Divider textAlign="left">Notas</Divider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Notas"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setFormOpen(false);
            setEditingExpense(null);
          }}>Cancelar</Button>
          <Button variant="contained" onClick={submitExpense}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(paymentTarget)} onClose={() => setPaymentTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Registrar pago</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField size="small" type="number" label="Importe" value={paymentForm.amount} onChange={(e) => setPaymentForm((f) => ({ ...f, amount: e.target.value }))} />
            <TextField size="small" type="date" label="Fecha de pago" InputLabelProps={{ shrink: true }} value={paymentForm.paymentDate} onChange={(e) => setPaymentForm((f) => ({ ...f, paymentDate: e.target.value }))} />
            <TextField size="small" label="Notas" value={paymentForm.notes} onChange={(e) => setPaymentForm((f) => ({ ...f, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentTarget(null)}>Cancelar</Button>
          <Button variant="contained" onClick={submitPayment}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(historyTarget)} onClose={() => setHistoryTarget(null)} maxWidth="md" fullWidth>
        <DialogTitle>Historial: {historyTarget?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ height: 320, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="periodStart" />
                <YAxis />
                <ChartTooltip />
                <Line type="monotone" dataKey="amount" stroke="#1976d2" name="Importe" />
                <Line type="monotone" dataKey="paidAmount" stroke="#2e7d32" name="Pagado" />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryTarget(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
